/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { Box, Checkbox, Field, Grid, Heading, Label, Text } from 'theme-ui';
import { toast } from 'react-toastify';
import Button from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import CustomDatePicker from '@/components/CustomDatePicker';
import { GrayFormikField } from '@/components/GrayFormikField';
import {
    useSendNewsletterBroadcastMutation,
    useTestSendNewsletterBroadcastMutation,
} from '@/store/apiService';
import { getErrorMessage } from '@/utils/helpers';

const COMPANY_STATUS_OPTIONS: { id: number; label: string }[] = [
    { id: 1, label: 'Registered' },
    { id: 2, label: 'Awaiting approval' },
    { id: 3, label: 'Approved' },
    { id: 4, label: 'Inactive' },
    { id: 5, label: 'Closed' },
    { id: 6, label: 'Not approved' },
];

const UNSUBSCRIBE_PLACEHOLDER = '{{unsubscribe}}';

interface FormValues {
    CompanyStatusIds: number[];
    IncludeCompanyOwners: boolean;
    IncludeCompanyEmails: boolean;
    Subject: string;
    HtmlBody: string;
    StartSendingAt: Date | null;
    TestRecipientEmail: string;
    IsSystemNotice: boolean;
}

const initialValues: FormValues = {
    CompanyStatusIds: [],
    IncludeCompanyOwners: true,
    IncludeCompanyEmails: false,
    Subject: '',
    HtmlBody: '',
    StartSendingAt: null,
    TestRecipientEmail: '',
    IsSystemNotice: false,
};

const formatDateTime = (value: Date | string | null | undefined) => {
    if (!value) return '';
    const date = typeof value === 'string' ? new Date(value) : value;
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

export const NewsletterBroadcast: React.FC = () => {
    const [sendBroadcast, sendBroadcastState] = useSendNewsletterBroadcastMutation();
    const [testSend, testSendState] = useTestSendNewsletterBroadcastMutation();
    const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);

    return (
        <Formik initialValues={initialValues} onSubmit={() => {}}>
            {(formProps) => {
                const { values, setFieldValue } = formProps;

                const toggleStatus = (statusId: number) => {
                    const next = values.CompanyStatusIds.includes(statusId)
                        ? values.CompanyStatusIds.filter((id) => id !== statusId)
                        : [...values.CompanyStatusIds, statusId];
                    setFieldValue('CompanyStatusIds', next);
                };

                const startIsInThePast =
                    !!values.StartSendingAt && values.StartSendingAt.getTime() < Date.now();

                const noRecipientTypeSelected =
                    !values.IncludeCompanyOwners && !values.IncludeCompanyEmails;

                const canSend =
                    values.CompanyStatusIds.length > 0 &&
                    !noRecipientTypeSelected &&
                    !!values.Subject &&
                    !!values.HtmlBody &&
                    !startIsInThePast;

                const selectedStatusLabels = values.CompanyStatusIds
                    .map((id) => COMPANY_STATUS_OPTIONS.find((o) => o.id === id)?.label)
                    .join(', ');

                const recipientDescription =
                    values.IncludeCompanyOwners && values.IncludeCompanyEmails
                        ? 'every company owner and each company\'s own contact email'
                        : values.IncludeCompanyOwners
                          ? 'every company owner'
                          : 'each company\'s own contact email';

                const scheduleSentence = values.StartSendingAt
                    ? `Sending starts ${formatDateTime(values.StartSendingAt)}`
                    : 'Sending starts immediately';

                return (
                    <Form>
                        {confirmationModalVisible ? (
                            <ConfirmationModal
                                loading={sendBroadcastState.isLoading}
                                headingText="Send newsletter?"
                                content={`This will email ${recipientDescription} of every company with status: ${selectedStatusLabels}. ${scheduleSentence}, throttled to 100 messages every 5 minutes.${
                                    values.IsSystemNotice
                                        ? ' This is marked as a system notice, so it will also go to admins who unsubscribed from the newsletter.'
                                        : ''
                                }`}
                                onNo={() => setConfirmationModalVisible(false)}
                                onYes={() => {
                                    sendBroadcast({
                                        CompanyStatusIds: values.CompanyStatusIds,
                                        IncludeCompanyOwners: values.IncludeCompanyOwners,
                                        IncludeCompanyEmails: values.IncludeCompanyEmails,
                                        Subject: values.Subject,
                                        HtmlBody: values.HtmlBody,
                                        IsSystemNotice: values.IsSystemNotice,
                                        ...(values.StartSendingAt
                                            ? {
                                                  StartSendingAt:
                                                      values.StartSendingAt.toISOString(),
                                              }
                                            : {}),
                                    })
                                        .unwrap()
                                        .then((res) => {
                                            const schedule = res.FirstSendDate
                                                ? ` Sending starts ${formatDateTime(
                                                      res.FirstSendDate
                                                  )}.`
                                                : '';
                                            const unsubscribed = res.TotalUnsubscribed
                                                ? ` ${res.TotalUnsubscribed} unsubscribed recipient(s) skipped.`
                                                : '';
                                            toast.success(
                                                `Queued ${res.TotalRecipients} recipient(s) in ${res.TotalBatches} batch(es).${schedule}${unsubscribed}`,
                                                { autoClose: 8000 }
                                            );
                                        })
                                        .catch((err) => {
                                            toast.error(getErrorMessage(err), { autoClose: 8000 });
                                        });
                                    setConfirmationModalVisible(false);
                                }}
                            />
                        ) : null}

                        <Grid gap={3}>
                            <Heading>Send newsletter to company admins</Heading>

                            <Box>
                                <Label>Company statuses</Label>
                                {COMPANY_STATUS_OPTIONS.map((option) => (
                                    <Label key={option.id} sx={{ cursor: 'pointer' }}>
                                        <Checkbox
                                            checked={values.CompanyStatusIds.includes(option.id)}
                                            onChange={() => toggleStatus(option.id)}
                                        />
                                        {option.label}
                                    </Label>
                                ))}
                            </Box>

                            <Box>
                                <Label>Recipients</Label>
                                <Label sx={{ cursor: 'pointer' }}>
                                    <Checkbox
                                        checked={values.IncludeCompanyOwners}
                                        onChange={() =>
                                            setFieldValue(
                                                'IncludeCompanyOwners',
                                                !values.IncludeCompanyOwners
                                            )
                                        }
                                    />
                                    Company owners
                                </Label>
                                <Label sx={{ cursor: 'pointer' }}>
                                    <Checkbox
                                        checked={values.IncludeCompanyEmails}
                                        onChange={() =>
                                            setFieldValue(
                                                'IncludeCompanyEmails',
                                                !values.IncludeCompanyEmails
                                            )
                                        }
                                    />
                                    Company emails
                                </Label>
                                {noRecipientTypeSelected ? (
                                    <Text color="danger">
                                        Select at least one recipient type.
                                    </Text>
                                ) : (
                                    <Text sx={{ fontSize: 0, color: 'gray.6' }}>
                                        Company owners are each matching company&apos;s active
                                        admins. Company emails are each matching company&apos;s own
                                        contact email address.
                                    </Text>
                                )}
                            </Box>

                            <GrayFormikField name="Subject" type="text" label="Subject" required />

                            <Box>
                                <Label sx={{ cursor: 'pointer' }}>
                                    <Checkbox
                                        checked={values.IsSystemNotice}
                                        onChange={() =>
                                            setFieldValue('IsSystemNotice', !values.IsSystemNotice)
                                        }
                                    />
                                    This is a required system notice, not a newsletter
                                </Label>
                                <Text sx={{ fontSize: 0, color: 'gray.6' }}>
                                    Only for mandatory service/policy notices. When checked, this send
                                    also reaches admins who unsubscribed from the newsletter - never use
                                    this for marketing-style content.
                                </Text>
                            </Box>

                            <Box>
                                <Label htmlFor="StartSendingAt">Start sending at</Label>
                                <CustomDatePicker
                                    variation="normal"
                                    id="StartSendingAt"
                                    isClearable
                                    minDate={new Date()}
                                    placeholderText="Send immediately"
                                    field={{
                                        name: 'StartSendingAt',
                                        value: values.StartSendingAt,
                                    }}
                                    onChange={(value: Date | null) => {
                                        setFieldValue('StartSendingAt', value);
                                    }}
                                />
                                {startIsInThePast ? (
                                    <Text color="danger">
                                        The start date must be in the future.
                                    </Text>
                                ) : (
                                    <Text color="gray.6" sx={{ fontSize: 0 }}>
                                        Leave empty to start sending as soon as you confirm. The
                                        first batch goes out at this time, the rest follow every 5
                                        minutes.
                                    </Text>
                                )}
                            </Box>

                            <Box>
                                <Label>Upload HTML body</Label>
                                <Field
                                    backgroundColor="gray.1"
                                    type="file"
                                    accept=".html,text/html"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const file = e.currentTarget.files?.[0];
                                        if (!file) return;
                                        file.text().then((html) => {
                                            setFieldValue('HtmlBody', html);
                                        });
                                    }}
                                />
                                <Text color="gray.6" sx={{ fontSize: 0 }}>
                                    {`Put ${UNSUBSCRIBE_PLACEHOLDER} in the HTML where you want the unsubscribe link. It uses BokaMera's own newsletter group, separate from the newsletters companies send to their customers, and anyone who has unsubscribed from it is skipped.`}
                                </Text>
                            </Box>

                            {values.HtmlBody ? (
                                <Box>
                                    <Label>Preview</Label>
                                    <Box
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'gray.2',
                                            borderRadius: 'sm',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <iframe
                                            title="Newsletter preview"
                                            srcDoc={values.HtmlBody}
                                            sandbox=""
                                            sx={{
                                                width: '100%',
                                                height: '400px',
                                                border: 'none',
                                                display: 'block',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            ) : null}

                            <Box
                                p="3"
                                sx={{
                                    borderRadius: 'sm',
                                    borderStyle: 'solid',
                                    borderWidth: '1px',
                                    borderColor: 'gray.2',
                                }}
                            >
                                <Label>Test send</Label>
                                <Grid gap={2} columns={['1fr', '1fr auto']}>
                                    <GrayFormikField
                                        name="TestRecipientEmail"
                                        type="email"
                                        label="Recipient email"
                                    />
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        isLoading={testSendState.isLoading}
                                        disabled={
                                            !values.Subject ||
                                            !values.HtmlBody ||
                                            !values.TestRecipientEmail
                                        }
                                        onClick={() => {
                                            testSend({
                                                Subject: values.Subject,
                                                HtmlBody: values.HtmlBody,
                                                TestRecipientEmail: values.TestRecipientEmail,
                                                IsSystemNotice: values.IsSystemNotice,
                                            })
                                                .unwrap()
                                                .then(() => {
                                                    toast.success('Test email sent', {
                                                        autoClose: 5000,
                                                    });
                                                })
                                                .catch((err) => {
                                                    toast.error(getErrorMessage(err), {
                                                        autoClose: 8000,
                                                    });
                                                });
                                        }}
                                    >
                                        Send test
                                    </Button>
                                </Grid>
                            </Box>

                            {sendBroadcastState.isError ? (
                                <Text color="danger">
                                    {getErrorMessage(sendBroadcastState.error)}
                                </Text>
                            ) : null}

                            <Box>
                                <Button
                                    type="button"
                                    isLoading={sendBroadcastState.isLoading}
                                    disabled={!canSend}
                                    onClick={() => setConfirmationModalVisible(true)}
                                >
                                    Send newsletter
                                </Button>
                            </Box>
                        </Grid>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default NewsletterBroadcast;
